import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Brain, Search, Trash2, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { api, FactItem, MemoryCategory } from '../api/client'
import { EmptyState } from '../components/ui/Empty'
import { ErrorCard } from '../components/ui/ErrorCard'
import { RowSkeleton } from '../components/ui/Skeleton'
import { useTelegram } from '../hooks/useTelegram'

type CategoryFilter = 'all' | MemoryCategory

const CATEGORY_TABS: { value: CategoryFilter; label: string }[] = [
  { value: 'all',      label: 'Всё' },
  { value: 'personal', label: 'Личное' },
  { value: 'work',     label: 'Работа' },
  { value: 'business', label: 'Бизнес' },
  { value: 'misc',     label: 'Прочее' },
]

const CATEGORY_LABEL: Record<MemoryCategory, string> = {
  personal: 'Личное',
  work:     'Работа',
  business: 'Бизнес',
  misc:     'Прочее',
}

function formatDay(iso: string): string {
  return new Date(iso).toLocaleDateString('ru', { day: 'numeric', month: 'long' })
}

function pluralFacts(n: number): string {
  const mod10 = n % 10, mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return 'факт'
  if ([2, 3, 4].includes(mod10) && ![12, 13, 14].includes(mod100)) return 'факта'
  return 'фактов'
}

function buildMemoryUrl(q: string, category: CategoryFilter): string {
  const params = new URLSearchParams()
  if (q) params.set('q', q)
  if (category !== 'all') params.set('category', category)
  params.set('limit', '50')
  const qs = params.toString()
  return `/api/memory${qs ? `?${qs}` : ''}`
}

// ── Edit Bottom Sheet ─────────────────────────────────────────────────────────

interface EditSheetProps {
  fact: FactItem
  onSave: (content: string) => void
  onCancel: () => void
  isSaving: boolean
}

function EditSheet({ fact, onSave, onCancel, isSaving }: EditSheetProps) {
  const [value, setValue] = useState(fact.content)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    // Focus after animation frame so transition plays first
    const id = requestAnimationFrame(() => textareaRef.current?.focus())
    return () => cancelAnimationFrame(id)
  }, [])

  const handleSave = () => {
    const trimmed = value.trim()
    if (trimmed && trimmed !== fact.content) onSave(trimmed)
    else if (trimmed === fact.content) onCancel()
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') onCancel()
  }

  // Через портал в body, как лист в календаре. Внутри страницы этот лист
  // рисовался под таб-баром: у обоих z-50, а таб-бар идёт в разметке позже —
  // при равном z-index побеждает он. Виден оставался только затемняющий фон
  // (z-40), из-за чего казалось, что «ничего не открывается, только блюр».
  // Портал кладёт лист в конец body, то есть заведомо поверх приложения.
  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0"
        style={{
          zIndex: 60,
          background: 'rgba(0,0,0,0.45)',
          backdropFilter: 'blur(4px)',
        }}
        onClick={onCancel}
      />

      {/* Sheet */}
      <div
        className="fixed bottom-0 left-0 right-0 px-4 pb-6 pt-5"
        style={{
          zIndex: 61,
          background: 'var(--surface-2)',
          borderRadius: '16px 16px 0 0',
          animation: 'slideUp 220ms cubic-bezier(0.32,0.72,0,1)',
        }}
      >
        {/* Drag handle */}
        <div className="w-10 h-1 rounded-full bg-line-1 mx-auto mb-4" />

        <p className="text-sm font-medium text-ink-2 mb-2">Редактировать факт</p>

        <textarea
          ref={textareaRef}
          value={value}
          onChange={e => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          rows={4}
          className="w-full text-sm text-ink leading-relaxed resize-none outline-none transition-colors p-3"
          style={{
            background: 'var(--surface-1)',
            border: '1px solid var(--line-1)',
            borderRadius: 10,
          }}
          onFocus={e => (e.currentTarget.style.borderColor = 'var(--accent)')}
          onBlur={e => (e.currentTarget.style.borderColor = 'var(--line-1)')}
        />

        <div className="flex gap-2 mt-3">
          <button
            onClick={onCancel}
            className="flex-1 py-2.5 rounded-xl text-sm font-medium text-ink-1 transition-colors btn-active"
            style={{ background: 'var(--surface-1)', border: '1px solid var(--line-1)' }}
          >
            Отмена
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving || !value.trim()}
            className="flex-1 py-2.5 rounded-xl text-sm font-medium text-white transition-colors btn-active disabled:opacity-50"
            style={{ background: 'var(--accent)' }}
          >
            {isSaving ? 'Сохранение…' : 'Сохранить'}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes slideUp {
          from { transform: translateY(100%); }
          to   { transform: translateY(0); }
        }
      `}</style>
    </>,
    document.body,
  )
}

// ── Main component ────────────────────────────────────────────────────────────

export function Memory() {
  const qc = useQueryClient()
  const { haptic, confirm, close } = useTelegram()

  const [inputValue, setInputValue] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [category, setCategory] = useState<CategoryFilter>('all')
  const [activeTag, setActiveTag] = useState<string | null>(null)
  const [editingFact, setEditingFact] = useState<FactItem | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => setDebouncedSearch(inputValue.trim()), 300)
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current) }
  }, [inputValue])

  const handleCategoryChange = (value: CategoryFilter) => {
    setCategory(value)
    setActiveTag(null)
  }

  const handleClearSearch = () => {
    setInputValue('')
    setDebouncedSearch('')
  }

  const { data, isLoading, error, refetch } = useQuery<FactItem[]>({
    queryKey: ['memory', debouncedSearch, category],
    queryFn: () => api.get(buildMemoryUrl(debouncedSearch, category)),
    staleTime: 5 * 60_000,
    placeholderData: keepPreviousData,
  })

  const { data: allData } = useQuery<FactItem[]>({
    queryKey: ['memory', '', 'all'],
    queryFn: () => api.get('/api/memory?limit=50'),
    staleTime: 5 * 60_000,
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.delete(`/api/memory/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['memory'] })
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, content }: { id: number; content: string }) =>
      api.patch<FactItem>(`/api/memory/${id}`, { content }),
    onMutate: async ({ id, content }) => {
      await qc.cancelQueries({ queryKey: ['memory'] })
      const snapshots = qc.getQueriesData<FactItem[]>({ queryKey: ['memory'] })
      qc.setQueriesData<FactItem[]>({ queryKey: ['memory'] }, old =>
        old?.map(f => (f.id === id ? { ...f, content } : f)) ?? old,
      )
      return { snapshots }
    },
    onError: (_err, _vars, ctx) => {
      ctx?.snapshots.forEach(([key, val]) => qc.setQueryData(key, val))
    },
    onSuccess: () => {
      setEditingFact(null)
      qc.invalidateQueries({ queryKey: ['memory'] })
    },
  })

  const handleDelete = (fact: FactItem) => {
    haptic.impactOccurred('light')
    confirm('Удалить этот факт?', ok => {
      if (ok) deleteMutation.mutate(fact.id)
    })
  }

  const handleCardTap = (fact: FactItem) => {
    haptic.impactOccurred('light')
    setEditingFact(fact)
  }

  const handleTagClick = (tag: string, e: React.MouseEvent) => {
    e.stopPropagation()
    haptic.impactOccurred('light')
    setActiveTag(prev => prev === tag ? null : tag)
  }

  const filtered = (data ?? []).filter(f => {
    if (activeTag && !(f.tags ?? []).includes(activeTag)) return false
    return true
  })

  const totalCount = allData?.length ?? 0
  // Счётчик в шапке отражает текущий фильтр, а не общее число фактов
  const shownCount = (category === 'all' && !debouncedSearch && !activeTag)
    ? totalCount
    : filtered.length
  const isEmpty = !isLoading && !error && totalCount === 0
  const isSearchEmpty = !isLoading && !error && totalCount > 0 && filtered.length === 0

  return (
    <div className="flex flex-col min-h-full page-enter">
      <div className="sticky top-0 sticky-header z-10 px-4 pt-5 pb-3">
        <p className="text-h1 text-ink">Память</p>
        {totalCount > 0 && (
          <p className="text-sm text-ink-2 mt-0.5">
            {shownCount} {pluralFacts(shownCount)}
          </p>
        )}

        {/* Search field */}
        {totalCount > 0 && (
          <div className="relative mt-3">
            <Search
              size={15}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-2 pointer-events-none"
            />
            <input
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
              placeholder="Поиск по памяти..."
              style={{ borderRadius: 10 }}
              className="w-full bg-surface-2 border border-line-1 text-ink text-sm pl-9 pr-8 py-2.5 outline-none transition-colors focus:border-accent focus:bg-surface-1 placeholder:text-ink-2"
            />
            {inputValue && (
              <button
                onClick={handleClearSearch}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-ink-2 hover:text-ink transition-colors p-0.5 btn-active"
                aria-label="Очистить поиск"
              >
                <X size={14} />
              </button>
            )}
          </div>
        )}

        {/* Category chips */}
        {totalCount > 0 && !debouncedSearch && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {CATEGORY_TABS.map(({ value, label }) => (
              <button
                key={value}
                onClick={() => handleCategoryChange(value)}
                className={`btn-active px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                  category === value
                    ? 'bg-accent-dim text-accent border border-accent'
                    : 'bg-surface-2 text-ink-1 border border-line-1'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        )}

        {/* Active tag filter */}
        {activeTag && (
          <div className="flex items-center gap-2 mt-2">
            <span className="text-xs text-ink-2">Тег:</span>
            <button
              onClick={() => setActiveTag(null)}
              className="flex items-center gap-1 px-2 py-1 rounded-full bg-accent-dim border border-accent text-xs text-accent font-medium"
            >
              #{activeTag}
              <X size={11} />
            </button>
          </div>
        )}
      </div>

      <div className="flex-1 px-4 pb-6 space-y-2">
        {isLoading && Array.from({ length: 4 }).map((_, i) => <RowSkeleton key={i} />)}

        {error && <ErrorCard onRetry={refetch} />}

        {isEmpty && (
          <EmptyState
            icon={<Brain size={48} />}
            title="Память пуста"
            description="Расскажи о себе — я запомню и буду учитывать"
            cta="Открыть чат с Delo"
            onCta={close}
          />
        )}

        {isSearchEmpty && (
          debouncedSearch ? (
            <EmptyState
              icon={<Search size={48} />}
              title="Ничего не найдено"
              description="Попробуй изменить запрос"
            />
          ) : (
            <EmptyState
              icon={<Brain size={48} />}
              title="В этой категории пусто"
              description="Попробуй другую или добавь новый факт"
            />
          )
        )}

        {filtered.map(fact => (
          <div
            key={fact.id}
            onClick={() => handleCardTap(fact)}
            className="bg-surface-1 border border-line-1 border-l-2 border-l-accent rounded-lg p-4 flex items-start gap-3 cursor-pointer btn-active"
          >
            <div className="flex-1 min-w-0">
              <p className="text-base text-ink leading-relaxed">{fact.content}</p>
              <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                <p className="text-xs text-ink-2">{formatDay(fact.created_at)}</p>
                {fact.categories?.map(c => (
                  <span
                    key={c}
                    className="text-xs text-accent bg-accent-dim px-1.5 py-0.5 rounded-md"
                  >
                    {CATEGORY_LABEL[c]}
                  </span>
                ))}
                {(fact.tags ?? []).map(tag => (
                  <button
                    key={tag}
                    onClick={e => handleTagClick(tag, e)}
                    className={`text-[11px] px-1.5 py-0.5 rounded-md border btn-active transition-colors ${
                      activeTag === tag
                        ? 'bg-accent-dim border-accent text-accent'
                        : 'bg-surface-2 border-line-1 text-ink-2'
                    }`}
                  >
                    #{tag}
                  </button>
                ))}
              </div>
            </div>
            <button
              onClick={e => { e.stopPropagation(); handleDelete(fact) }}
              className="btn-active shrink-0 text-ink-2 p-1 -mr-1 -mt-0.5 rounded hover:text-danger transition-colors"
              aria-label="Удалить"
            >
              <Trash2 size={15} />
            </button>
          </div>
        ))}
      </div>

      {editingFact && (
        <EditSheet
          fact={editingFact}
          isSaving={updateMutation.isPending}
          onSave={content => updateMutation.mutate({ id: editingFact.id, content })}
          onCancel={() => setEditingFact(null)}
        />
      )}
    </div>
  )
}
